/*
 * Real proctoring data to the review's feed. Reads the timeline that
 * GET /api/reporting/v1/proctoring-review returns and turns each placed
 * piece of evidence into a FeedItem the rules in `proctoring.js` can count,
 * word and headline. Every time is in seconds from `clock.origin`.
 *
 * What is decided here (the plan's "Mapping in fromTimeline.js"):
 *  - captures: severity from the image AI's verdict status, words from its type;
 *    a client-side flag gets a note; a legacy snapshot with only a
 *    criticalReason is worth a look, never critical; a snapshot with neither
 *    was not analysed and is not a check, so it is left out;
 *  - profile check: isCritical is critical;
 *  - tab switches: a count per question, worth a look once the running total
 *    passes the limit, never critical (no times, no durations);
 *  - audio: speech outside the answers is worth a look from AUDIO_MIN seconds;
 *    it becomes "Possible outside help" (critical) only when the audio review is
 *    suspicious and one of its placed quotes lies within HELP_WINDOW seconds;
 *  - the audio review's quotes are their own kind;
 *  - answer integrity is experimental: worth a look at most, never critical;
 *  - screen snapshots: severity from their status;
 *  - the recordings' start, end and gaps are clear video items;
 *  - a session after the first (the candidate re-entered the assessment) is a restart, worth a look;
 *    only the first reference image is listed, at the clock's start; a later session's retaken photo is
 *    shown on that session's restart, not as a second reference image.
 * Nothing unplaced becomes an item: an item needs a finite `at` and a
 * placement other than "unplaced"; `audio.unplaced[]` is never read. There is
 * no speaker detection, so audio is never described as another voice.
 */
import { clockOf, span, tenths } from "./format";
import { AI_LIKELY, AUDIO_MIN, byFeedOrder, byText, segOf } from "./proctoring";

/* ---- the payload (GET /api/reporting/v1/proctoring-review) ---- */

/**
 * @typedef {Object} ImageVerdict
 * @property {"cheating"|"maybeCheating"|"notCheating"} status
 * @property {"allGood"|"noPerson"|"distracted"|"anotherPerson"|"collaboration"|"differentPerson"|"unauthorizedDevice"|"tampering"} [type]
 * @property {string} [remark]
 * @property {"ai"|"clientFlag"} [source]
 */

/**
 * A camera capture or a screen snapshot.
 * @typedef {Object} TimelineSnapshot
 * @property {string} key
 * @property {string} [sessionId]
 * @property {number|null} at
 * @property {string} [imageUrl]
 * @property {ImageVerdict|null} [verdict]
 * @property {string|null} [criticalReason]
 * @property {Object|null} [professionalReadiness]
 * @property {string} [placement]
 */

/**
 * @typedef {Object} TimelineQuestion
 * @property {string} key
 * @property {string} label          "Q1", the same labels the audio review uses
 * @property {string} [partKey]
 * @property {string} [type]
 * @property {number} from
 * @property {number} [to]
 * @property {string} [fromSource]
 * @property {boolean} [answered]
 * @property {number|null} [tabSwitches]  switches while it was on screen; null when not known
 */

/**
 * @typedef {Object} ReviewInstance
 * @property {string} key
 * @property {string} quote
 * @property {string} [reason]
 * @property {"low"|"medium"|"high"} [severity]
 * @property {number|null} at
 * @property {number} [dur]
 * @property {string} [questionKey]
 * @property {"line"|"quote"|"approx"|"question"} [placement]
 * @property {string} [line]   the transcript line it was found on
 */

/**
 * A presence value: "captured" | "missing" (turned on, nothing captured) | "off" | "unavailable" (video-stream
 * unreachable). Booleans are accepted as captured / off.
 * @typedef {"captured"|"missing"|"off"|"unavailable"|boolean} Presence
 */

/**
 * The `data` of GET /api/reporting/v1/proctoring-review (version "proctoring-review-v1").
 * @typedef {Object} ProctoringTimeline
 * @property {string} [version]
 * @property {{origin: string, length: number, offsetMs: number|null, offsetSource: string}} clock
 *   origin is the earliest captured moment, so every `at` is within [0, length]
 * @property {{userRecording: Presence, screenRecording: Presence, audioRecording: Presence, imageProctoring: Presence,
 *   transcript: Presence, review: Presence, integrity: Presence}} presence
 * @property {{total: number|null, max: number|null}} [tabs]  switches in the round, and the limit
 * @property {Array<{key: string, kind: string, name: string, from: number, to: number}>} parts
 * @property {TimelineQuestion[]} questions  an unanswered question has from/to null
 * @property {TimelineSnapshot[]} captures
 * @property {Array<{key: string, sessionId?: string, at?: number, imageUrl?: string, placement?: string}>} [references]  no `at`
 * @property {Array<{key: string, sessionId?: string, at?: number, profileUrl?: string, sessionUrl?: string,
 *   isCritical?: boolean, criticalReason?: string, placement?: string}>} [profiles]
 * @property {TimelineSnapshot[]} [screens]
 * @property {{status?: string, vadCompleted: boolean,
 *   metrics: {totalAudioSec?: number, speechSec?: number, speechQuestionSpeechSec?: number, unexpectedSpeechSec?: number},
 *   markers: Array<{key: string, at: number, dur: number, questionKey?: string, placement?: string}>,
 *   speech?: Array<{at: number, dur: number}>,
 *   review: {verdict: "clear"|"inconclusive"|"suspicious", confidence: string, summary: string, model?: string, version?: string,
 *     instances: ReviewInstance[]}|null,
 *   transcript: Array<{line?: string, from: number, to: number, text: string}>, unplaced: Array}} [audio]
 * @property {Array<{key: string, questionKey?: string, at: number, dur?: number, modality: "spoken"|"typed",
 *   aiGenerated: number|null, readAloud: number|null, thoughtThrough?: number|null, confidence?: number|null,
 *   readingScore?: number|null, pasteLikeScore?: number|null, reasons?: string|null, markers?: string[], placement?: string}>} [integrity]
 * @property {{user?: Recording, screen?: Recording, audio?: Recording}} [recordings]
 * @property {Array<string|{key: string, name?: string, why?: string}>} [notCaptured]
 * @property {Object} [sources]
 * @property {string|null} [linksExpireAt]
 */

/**
 * @typedef {Object} Recording
 * @property {number|Array<{from: number, to: number}>} [sessions]  how many sessions (older payloads: when each ran)
 * @property {Array<{id?: string, from: number, to: number, chunks?: number}>} [runs]  each session's recording, in time order
 * @property {number} [from]   the recording's first moment
 * @property {number} [to]     its last
 * @property {number} [chunks]
 * @property {number} [seconds]
 * @property {number} [snapshots]
 * @property {Array<{key?: string, from: number, to: number}>} [gaps]
 */

/* ---- the mapping ---- */

/** A quote from a suspicious audio review this close (seconds) to speech outside the answers makes it possible outside help. */
export const HELP_WINDOW = 15;
/** Tab switches allowed when the position does not set maxTabSwitch. */
export const DEFAULT_TAB_LIMIT = 2;

/**
 * Placements that put an item on the clock only roughly: "approx" and "question" from the audio review, and
 * "first-chunk" (a snapshot its session's chunks could not hold, placed from the first chunk's start).
 */
export const APPROX_PLACEMENTS = ["approx", "question", "first-chunk"];

/** The image AI's status to a severity. */
export const STATUS_SEVERITY = { cheating: "critical", maybeCheating: "review", notCheating: "clear" };

/** The image AI's verdict type, in words. */
export const CAPTURE_TYPE_WORD = {
  allGood: "Nothing unusual in view",
  noPerson: "No one in view",
  distracted: "Looking away from the screen",
  anotherPerson: "Another person in view",
  collaboration: "Signs of help from someone else",
  differentPerson: "Not the person in the reference image",
  unauthorizedDevice: "A phone or another device in view",
  tampering: "The camera blocked or tampered with",
};

/** Spellings the image AI has used for the same verdict, to the one the words are keyed by. */
export const CAPTURE_TYPE_ALIAS = { distraction: "distracted" };
const captureType = (type) => (type && CAPTURE_TYPE_ALIAS[type]) || type;

/** Capture verdicts where the candidate's own face was not there to match: no one, someone else, a covered camera. */
export const FACE_MISS = ["noPerson", "differentPerson", "tampering"];

/**
 * "Face matched x of y" for the header: of the captures the image analysis looked at, those where the candidate's
 * face was present. A phone or another person in view still has the candidate's face; the reference image and
 * snapshots without a verdict are not counted.
 * @param {Array} items  FeedItems (any kinds)
 * @returns {{matched: number, total: number}|null} null when no capture was analysed
 */
export function faceFacts(items) {
  const analysed = (items || []).filter((x) => x && x.kind === "capture" && x.analysed);
  if (!analysed.length) return null;
  return { matched: analysed.filter((x) => !FACE_MISS.includes(x.capture)).length, total: analysed.length };
}

export const CLIENT_FLAG_NOTE = "Flagged by the candidate's browser, not by the image analysis";
export const LEGACY_NOTE = "No image analysis verdict; flagged when the snapshot was taken";

/** What the platform never captures, as lanes the room hatches as "not captured". */
export const NOT_CAPTURED = [
  { k: "tabTimes", n: "Tab switch times", seg: "overall", why: "Only a count per question is kept, not when each switch happened" },
  { k: "fullscreen", n: "Full screen", seg: "overall", why: "Leaving full screen is not recorded" },
  { k: "focus", n: "Window focus", seg: "screen", why: "The window losing focus is not recorded" },
  { k: "display", n: "Second display", seg: "screen", why: "Displays connected are not recorded" },
  { k: "sharing", n: "Screen sharing", seg: "screen", why: "Screen sharing is not recorded" },
  { k: "screenshots", n: "Screenshots", seg: "screen", why: "Screenshot attempts are not recorded" },
  { k: "clipboard", n: "Copy and paste", seg: "overall", why: "Copy and paste attempts are not recorded" },
  { k: "typing", n: "Typing pattern", seg: "ai", why: "Keystrokes and typing speed are not recorded" },
];

/**
 * The not-captured lanes: the fixed list above, plus any the timeline names that it does not already hold.
 * @param {ProctoringTimeline} [timeline]
 * @returns {Array<{k: string, n: string, seg?: string, why?: string}>}
 */
export function notCapturedOf(timeline) {
  const out = NOT_CAPTURED.slice();
  const extra = (timeline && Array.isArray(timeline.notCaptured)) ? timeline.notCaptured : [];
  extra.forEach((x) => {
    const lane = typeof x === "string" ? { k: x, n: x } : { k: x.key || x.k, n: x.name || x.n || x.key || x.k, why: x.why };
    const same = (y) => String(y.k).toLowerCase() === String(lane.k).toLowerCase();
    if (lane.k && !out.some(same)) out.push(lane);
  });
  return out;
}

/**
 * A presence value from the timeline: "captured" | "missing" (turned on, nothing captured) | "off" (not turned on)
 * | "unavailable" (video-stream could not be reached). Booleans are read as captured / off.
 */
export const isCaptured = (v) => v === true || v === "captured";
/** The analysis was turned on for the round, whether or not anything was captured. */
export const isEnabled = (v) => v === true || v === "captured" || v === "missing";

/**
 * What the round was set to watch, in the rules' terms. Each analysis is on when the position (or link) turns it
 * on or the timeline says it was turned on (captured or missing). PositionConfig.aiProctoring is camera image AI,
 * so it is `image`; `ai` (answers checked for AI help) is answer integrity, which has no position setting.
 * The tab limit is the timeline's `tabs.max` when it has one, else the position's maxTabSwitch, else 2.
 * @param {Object} [linkConfig]
 * @param {Object} [positionConfig]
 * @param {Object} [presence]  timeline.presence
 * @param {{total?: number|null, max?: number|null}} [tabs]  timeline.tabs
 * @returns {import("./proctoring").ProctorSettings}
 */
export function settingsFrom(linkConfig, positionConfig, presence, tabs) {
  const L = linkConfig || {};
  const P = positionConfig || {};
  const H = presence || {};
  const fromTimeline = tabs && tabs.max != null ? Number(tabs.max) : NaN;
  const fromPosition = P.maxTabSwitch == null ? NaN : Number(P.maxTabSwitch);
  const limit = Number.isFinite(fromTimeline) && fromTimeline >= 0 ? fromTimeline : fromPosition;
  return {
    tab: true,
    tabLimit: Number.isFinite(limit) && limit >= 0 ? limit : DEFAULT_TAB_LIMIT,
    /* never captured: see NOT_CAPTURED */
    full: false,
    copy: false,
    video: !!(isEnabled(H.userRecording) || isEnabled(H.screenRecording) || L.saveProctoredVideo || P.saveProctoredVideo),
    image: !!(P.aiProctoring || isEnabled(H.imageProctoring)),
    audio: !!(P.audioProctoring || isEnabled(H.audioRecording)),
    screen: !!(P.screenProctoring || isEnabled(H.screenRecording)),
    ai: isEnabled(H.integrity),
  };
}

const isPlaced = (x) => !!x && Number.isFinite(x.at) && x.placement !== "unplaced";
/** A recording's sessions in time order: its `runs`, or (older payloads) a `sessions` list; [] for a count. */
const runsOf = (r) => {
  const list = r && Array.isArray(r.runs) ? r.runs : (r && Array.isArray(r.sessions) ? r.sessions : []);
  return list.filter((x) => x && Number.isFinite(x.from) && Number.isFinite(x.to)).slice().sort((a, b) => a.from - b.from);
};
const hasText = (s) => typeof s === "string" ? s.trim().length > 0 : !!s;
const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const in100 = (p) => `${Math.round(Number(p) * 100)} in 100`;

/**
 * Every placed piece of evidence on the timeline as a FeedItem, in time order.
 * @param {ProctoringTimeline} timeline
 * @param {Object} [options]
 * @param {Object} [options.reportData]  the page's report-data; accepted for the room's call site, not read yet
 * @param {import("./proctoring").ProctorSettings} [options.settings]  from settingsFrom(); defaults from timeline.presence
 * @returns {import("./proctoring").FeedItem[]}
 */
export function feedFromTimeline(timeline, options = {}) {
  const T = timeline || {};
  const S = options.settings || settingsFrom(null, null, T.presence, T.tabs);
  const questions = (T.questions || []).filter((q) => q && Number.isFinite(q.from))
    .slice().sort((a, b) => a.from - b.from || byText(a.label, b.label));
  const byKey = new Map(questions.map((q) => [q.key, q]));
  const questionOf = (q) => {
    if (!q) return {};
    const n = /(\d+)/.exec(q.label || "");
    return { questionKey: q.key, questionLabel: q.label, question: n ? Number(n[1]) : undefined };
  };
  const byTime = (at) => questions.find((q) => at >= q.from && Number.isFinite(q.to) && at < q.to) || null;
  const inQuestion = (q) => (q && q.questionLabel ? `In ${q.questionLabel}` : null);

  const out = [];
  const push = (item) => {
    out.push({
      id: item.key,
      seg: segOf(item.kind),
      flagged: item.severity !== "clear",
      detail: null,
      ...item,
    });
  };
  const approxOf = (x) => (APPROX_PLACEMENTS.includes(x.placement) ? { approx: true } : {});

  /* each session's recording in time order (recordings.user, else screen, else audio): a session after the first
     is a restart, the candidate having re-entered the assessment */
  const R = T.recordings || {};
  const runs = [R.user, R.screen, R.audio].map(runsOf).find((list) => list.length) || [];
  const runStart = (sessionId) => {
    const run = sessionId != null ? runs.find((x) => x.id === sessionId) : null;
    return run ? run.from : null;
  };

  /* the reference images, earliest session first: only the first is listed (taken before the round, so at the
     clock's start); a later session's is a retake, shown on that session's restart below */
  const firstOf = (sessionId) => {
    const ats = (T.captures || []).filter((c) => c && c.sessionId === sessionId && isPlaced(c)).map((c) => c.at);
    return sessionId != null && ats.length ? Math.min(...ats) : 0;
  };
  const refAt = (r) => {
    if (Number.isFinite(r.at)) return r.at;
    const start = runStart(r.sessionId);
    return start != null ? start : firstOf(r.sessionId);
  };
  const refs = (T.references || []).filter((r) => r && r.placement !== "unplaced")
    .map((r) => ({ r, at: refAt(r) }))
    .sort((a, b) => a.at - b.at || byText(a.r.key, b.r.key));
  if (refs.length) {
    const { r, at } = refs[0];
    push({ key: r.key, kind: "capture", at: Number.isFinite(r.at) ? at : 0, severity: "clear", what: "Reference image",
      capture: "reference", detail: "Taken before the round; captures are checked against it" });
  }

  /* a session after the first: the candidate re-entered the assessment. From the recordings' runs; without them,
     from a later session's reference image, placed at that session's first capture. The retaken photo rides along. */
  const retakes = refs.slice(1);
  const restarts = runs.length > 1
    ? runs.slice(1).map((run, i) => ({ id: run.id, at: run.from, stoppedAt: runs[i].to, n: i + 2 }))
    : retakes.map(({ r, at }, i) => ({ id: r.sessionId, at, stoppedAt: null, n: i + 2 }));
  restarts.forEach((s) => {
    const retake = retakes.find(({ r }) => r.sessionId != null && r.sessionId === s.id) || null;
    push({ key: `session:${s.id != null ? s.id : s.n}`, kind: "session", at: s.at, severity: "review",
      what: `Session ${s.n} began: the candidate re-entered the assessment`,
      detail: `${s.stoppedAt != null ? `The recording stopped at ${clockOf(s.stoppedAt)} and started again here` : "The recording started again here"}; a new reference photo was taken`,
      ...(retake ? { reference: retake.r.key } : {}) });
  });

  /* the profile check: the session photo against the profile photo; only a check that ran is listed */
  (T.profiles || []).forEach((p) => {
    if (!p || p.placement === "unplaced" || typeof p.isCritical !== "boolean") return;
    push({ key: p.key, kind: "profile", at: Number.isFinite(p.at) ? p.at : 0, severity: p.isCritical ? "critical" : "clear",
      what: p.isCritical ? "Did not match the profile photo" : "Matched the profile photo",
      detail: p.isCritical && hasText(p.criticalReason) ? p.criticalReason : null });
  });

  /* camera captures */
  (T.captures || []).filter(isPlaced).forEach((c) => {
    const v = c.verdict || null;
    const severity = v ? STATUS_SEVERITY[v.status] : undefined;
    const q = questionOf(byTime(c.at));
    if (severity) {
      push({ key: c.key, kind: "capture", at: c.at, severity,
        what: CAPTURE_TYPE_WORD[captureType(v.type)] || (severity === "clear" ? CAPTURE_TYPE_WORD.allGood : "Flagged by the image analysis"),
        detail: hasText(v.remark) ? v.remark : (hasText(c.criticalReason) ? c.criticalReason : null),
        capture: captureType(v.type) || "unverified",
        analysed: true,
        ...(v.source === "clientFlag" ? { note: CLIENT_FLAG_NOTE } : {}),
        ...q, ...approxOf(c) });
    } else if (hasText(c.criticalReason)) {
      /* legacy: flagged when taken, never analysed; worth a look, not critical */
      push({ key: c.key, kind: "capture", at: c.at, severity: "review", what: "Snapshot flagged",
        detail: String(c.criticalReason), capture: "unverified", note: LEGACY_NOTE, ...q, ...approxOf(c) });
    }
    /* neither a verdict nor a reason: not analysed, so not a check */
  });

  /* tab switches: a count per question, the running total against the limit; no times, so never critical */
  let total = 0;
  questions.forEach((q) => {
    const n = Number(q.tabSwitches);
    if (q.tabSwitches == null || !Number.isFinite(n) || n <= 0) return;
    total += n;
    const over = S.tab !== false && total > S.tabLimit;
    push({ key: `tabcount:${q.key}`, kind: "tabcount", at: q.from, severity: over ? "review" : "clear",
      ...(Number.isFinite(q.to) ? { dur: Math.max(0, q.to - q.from) } : {}),
      what: `${count(n, "tab switch", "tab switches")} during ${q.label}${over ? `, past the ${S.tabLimit} allowed` : ""}`,
      detail: `${total} in the round by then; exact times not captured`,
      approx: true, ...questionOf(q) });
  });
  /* only the session total is known (no per-question counts): one check for the whole round */
  const sessionTotal = Number(T.tabs && T.tabs.total);
  const perQuestion = questions.some((q) => q.tabSwitches != null && Number.isFinite(Number(q.tabSwitches)));
  if (!perQuestion && T.tabs && T.tabs.total != null && Number.isFinite(sessionTotal) && sessionTotal >= 0) {
    const over = S.tab !== false && sessionTotal > S.tabLimit;
    push({ key: "tabcount:session", kind: "tabcount", at: 0, severity: over ? "review" : "clear",
      what: `${count(sessionTotal, "tab switch", "tab switches")} in the round${over ? `, past the ${S.tabLimit} allowed` : ""}`,
      detail: "Which questions they happened in, and when, was not recorded",
      approx: true });
  }

  /* audio: speech outside the answers, and the review's quotes */
  const audio = T.audio || {};
  const review = audio.review || null;
  const suspicious = !!review && review.verdict === "suspicious";
  const instances = ((review && review.instances) || []).filter(isPlaced);
  // Only a quote placed on its own line, its own words or its m:ss can upgrade speech: one
  // placed at its question's start could be anywhere in that question.
  const pinned = instances.filter((i) => i.placement !== "question");
  const helpNear = (m, dur) => suspicious && pinned.some((i) =>
    i.at <= m.at + dur + HELP_WINDOW && i.at + (Number(i.dur) || 0) >= m.at - HELP_WINDOW);
  (audio.markers || []).filter(isPlaced).forEach((m) => {
    const dur = Math.max(0, Number(m.dur) || 0);
    const q = questionOf(byKey.get(m.questionKey) || byTime(m.at));
    let severity = "review";
    let what = `Speech outside answers for ${span(dur)}`;
    let why = null;
    if (dur < AUDIO_MIN) {
      severity = "clear";
      what = `Speech outside answers for ${tenths(dur)}, too short to flag`;
    } else if (helpNear(m, dur)) {
      severity = "critical";
      what = `Possible outside help for ${span(dur)}`;
      why = `a quote the audio review found suspicious is within ${HELP_WINDOW} s`;
    }
    const detail = [inQuestion(q), why].filter(Boolean).join("; ");
    push({ key: m.key, kind: "audio", at: m.at, dur, severity, what,
      detail: detail ? `${detail.charAt(0).toUpperCase()}${detail.slice(1)}` : null, ...q, ...approxOf(m) });
  });
  instances.forEach((i) => {
    const q = questionOf(byKey.get(i.questionKey) || byTime(i.at));
    const severity = suspicious && (i.severity === "high" || i.severity === "medium") ? "critical" : "review";
    push({ key: i.key, kind: "quote", at: i.at, severity,
      ...(Number.isFinite(i.dur) ? { dur: i.dur } : {}),
      what: hasText(i.quote) ? String(i.quote).trim() : "A moment flagged by the audio review",
      detail: hasText(i.reason) ? i.reason : null, ...q, ...approxOf(i) });
  });

  /* answer integrity: experimental, worth a look at most */
  (T.integrity || []).filter(isPlaced).forEach((x) => {
    const q = questionOf(byKey.get(x.questionKey) || byTime(x.at));
    const signals = [];
    const words = [];
    if (Number(x.aiGenerated) >= AI_LIKELY) { signals.push("aiGenerated"); words.push(`likely AI-written (${in100(x.aiGenerated)})`); }
    if (x.modality === "spoken" && x.readAloud != null && Number(x.readAloud) >= AI_LIKELY) {
      signals.push("readAloud"); words.push(`likely read aloud (${in100(x.readAloud)})`);
    }
    const whose = q.questionLabel ? `Answer to ${q.questionLabel}` : "An answer";
    push({ key: x.key, kind: "integrity", at: x.at, severity: signals.length ? "review" : "clear",
      ...(Number.isFinite(x.dur) ? { dur: x.dur } : {}),
      what: signals.length ? `${whose} ${words.join(" and ")}` : `${whose}: nothing unusual`,
      detail: hasText(x.reasons) ? x.reasons : null,
      experimental: true, signals, ...q, ...approxOf(x) });
  });

  /* screen snapshots: severity from their status; the same not-analysed rule as captures */
  (T.screens || []).filter(isPlaced).forEach((s) => {
    const v = s.verdict || (s.status ? { status: s.status } : null);
    const severity = v ? STATUS_SEVERITY[v.status] : undefined;
    const q = questionOf(byTime(s.at));
    if (severity) {
      push({ key: s.key, kind: "screen", at: s.at, severity, snapshot: true,
        what: severity === "clear" ? "Nothing unusual on screen" : "Screen snapshot flagged",
        detail: hasText(v.remark) ? v.remark : (hasText(s.criticalReason) ? s.criticalReason : null),
        ...(v.source === "clientFlag" ? { note: CLIENT_FLAG_NOTE } : {}),
        ...q, ...approxOf(s) });
    } else if (hasText(s.criticalReason)) {
      push({ key: s.key, kind: "screen", at: s.at, severity: "review", snapshot: true, what: "Screen snapshot flagged",
        detail: String(s.criticalReason), note: LEGACY_NOTE, ...q, ...approxOf(s) });
    }
  });

  /* the recordings: start, end and each gap, all clear and left out of the figures */
  [["user", "Camera"], ["screen", "Screen"]].forEach(([which, name]) => {
    const r = T.recordings && T.recordings[which];
    if (!r) return;
    /* `runs` (or, older, a `sessions` list) says when each session ran; else `sessions` is a count with the
       recording's own from/to */
    const sessions = runsOf(r).length ? runsOf(r)
      : (Number.isFinite(r.from) && Number.isFinite(r.to) ? [{ from: r.from, to: r.to }] : []);
    if (!sessions.length) return;
    const start = Math.min(...sessions.map((x) => x.from));
    const end = Math.max(...sessions.map((x) => x.to));
    const gaps = (Array.isArray(r.gaps) ? r.gaps : []).filter((g) => g && Number.isFinite(g.from) && Number.isFinite(g.to));
    push({ key: `video:${which}:start`, kind: "video", at: start, severity: "clear", what: `${name} recording started` });
    push({ key: `video:${which}:end`, kind: "video", at: end, severity: "clear",
      what: `${name} recording ended after ${span(end - start)}, ${gaps.length ? count(gaps.length, "gap", "gaps") : "no gaps"}` });
    gaps.forEach((g) => {
      push({ key: g.key || `video:${which}:gap:${g.from.toFixed(3)}`, kind: "video", at: g.from, dur: Math.max(0, g.to - g.from),
        severity: "clear", what: `${name} recording gap of ${span(g.to - g.from)}`, detail: "Nothing was recorded here" });
    });
  });

  return out.sort(byFeedOrder);
}
